import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X, Bug, Sparkles, Heart, MoreHorizontal, Send, Check, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// === I18N labels ===
const L = {
  ar: {
    button: 'إرسال ملاحظة',
    title: 'شاركنا رأيك',
    intro: 'ملاحظاتك تساعدنا على تحسين فَلَك. كل رسالة تصل إلينا.',
    category: 'نوع الملاحظة',
    cat_bug: 'مشكلة', cat_feature: 'اقتراح', cat_praise: 'إشادة', cat_other: 'أخرى',
    name: 'اسمك (اختياري)',
    email: 'بريدك الإلكتروني (اختياري)',
    email_hint: 'حتى نتمكن من الرد عليك',
    message: 'ملاحظتك',
    msg_placeholder: 'اكتب ملاحظتك هنا...',
    send: 'إرسال',
    sending: 'جاري الإرسال...',
    cancel: 'إلغاء',
    via_email: 'أرسل عبر البريد بدلاً من ذلك',
    success_title: 'شكراً لك! 🌙',
    success_msg: 'وصلتنا ملاحظتك بسلام. سنأخذها بعين الاعتبار.',
    close: 'إغلاق',
    error: 'حدث خطأ، الرجاء المحاولة مرة أخرى.',
    msg_required: 'الرجاء كتابة ملاحظتك قبل الإرسال',
  },
  en: {
    button: 'Send Feedback',
    title: 'Share your thoughts',
    intro: 'Your feedback helps us improve Falak. Every message is read.',
    category: 'Type of feedback',
    cat_bug: 'Bug', cat_feature: 'Suggestion', cat_praise: 'Praise', cat_other: 'Other',
    name: 'Your name (optional)',
    email: 'Your email (optional)',
    email_hint: 'So we can reply to you',
    message: 'Your message',
    msg_placeholder: 'Write your feedback here...',
    send: 'Send',
    sending: 'Sending...',
    cancel: 'Cancel',
    via_email: 'Send via email instead',
    success_title: 'Thank you! 🌙',
    success_msg: 'Your feedback has been received. We truly appreciate it.',
    close: 'Close',
    error: 'Something went wrong. Please try again.',
    msg_required: 'Please write your message before sending',
  },
  id: {
    button: 'Kirim Masukan',
    title: 'Bagikan pendapat Anda',
    intro: 'Masukan Anda membantu kami menyempurnakan Falak. Setiap pesan dibaca.',
    category: 'Jenis masukan',
    cat_bug: 'Bug', cat_feature: 'Saran', cat_praise: 'Pujian', cat_other: 'Lainnya',
    name: 'Nama Anda (opsional)',
    email: 'Email Anda (opsional)',
    email_hint: 'Agar kami bisa membalas',
    message: 'Pesan Anda',
    msg_placeholder: 'Tulis masukan Anda di sini...',
    send: 'Kirim',
    sending: 'Mengirim...',
    cancel: 'Batal',
    via_email: 'Kirim via email',
    success_title: 'Terima kasih! 🌙',
    success_msg: 'Masukan Anda telah diterima dengan baik.',
    close: 'Tutup',
    error: 'Terjadi kesalahan, silakan coba lagi.',
    msg_required: 'Mohon tulis pesan Anda terlebih dahulu',
  },
  ur: {
    button: 'تاثرات بھیجیں',
    title: 'اپنی رائے شیئر کریں',
    intro: 'آپ کی تاثرات فلک کو بہتر بنانے میں ہماری مدد کرتی ہیں۔',
    category: 'تاثرات کی قسم',
    cat_bug: 'مسئلہ', cat_feature: 'تجویز', cat_praise: 'تعریف', cat_other: 'دیگر',
    name: 'آپ کا نام (اختیاری)',
    email: 'آپ کا ای میل (اختیاری)',
    email_hint: 'تاکہ ہم جواب دے سکیں',
    message: 'آپ کا پیغام',
    msg_placeholder: 'یہاں اپنی تاثرات لکھیں...',
    send: 'بھیجیں',
    sending: 'بھیجا جا رہا ہے...',
    cancel: 'منسوخ',
    via_email: 'بجائے ای میل کے ذریعے بھیجیں',
    success_title: 'شکریہ! 🌙',
    success_msg: 'آپ کی تاثرات موصول ہو گئی ہیں۔',
    close: 'بند کریں',
    error: 'کچھ غلط ہوا، براہ کرم دوبارہ کوشش کریں۔',
    msg_required: 'براہ کرم بھیجنے سے پہلے اپنا پیغام لکھیں',
  },
  fr: {
    button: 'Envoyer un avis',
    title: 'Partagez votre avis',
    intro: 'Vos commentaires nous aident à améliorer Falak. Chaque message est lu.',
    category: 'Type de commentaire',
    cat_bug: 'Bug', cat_feature: 'Suggestion', cat_praise: 'Éloge', cat_other: 'Autre',
    name: 'Votre nom (facultatif)',
    email: 'Votre e-mail (facultatif)',
    email_hint: 'Pour que nous puissions vous répondre',
    message: 'Votre message',
    msg_placeholder: 'Écrivez votre commentaire ici...',
    send: 'Envoyer',
    sending: 'Envoi...',
    cancel: 'Annuler',
    via_email: 'Envoyer par e-mail',
    success_title: 'Merci ! 🌙',
    success_msg: 'Votre message a bien été reçu.',
    close: 'Fermer',
    error: "Une erreur s'est produite. Veuillez réessayer.",
    msg_required: 'Veuillez rédiger votre message avant de l\'envoyer',
  },
  bn: {
    button: 'মতামত পাঠান',
    title: 'আপনার মতামত শেয়ার করুন',
    intro: 'আপনার মতামত ফালাককে উন্নত করতে সাহায্য করে।',
    category: 'মতামতের ধরন',
    cat_bug: 'বাগ', cat_feature: 'প্রস্তাব', cat_praise: 'প্রশংসা', cat_other: 'অন্যান্য',
    name: 'আপনার নাম (ঐচ্ছিক)',
    email: 'আপনার ইমেইল (ঐচ্ছিক)',
    email_hint: 'যাতে আমরা উত্তর দিতে পারি',
    message: 'আপনার বার্তা',
    msg_placeholder: 'এখানে আপনার মতামত লিখুন...',
    send: 'পাঠান',
    sending: 'পাঠানো হচ্ছে...',
    cancel: 'বাতিল',
    via_email: 'ইমেইলের মাধ্যমে পাঠান',
    success_title: 'ধন্যবাদ! 🌙',
    success_msg: 'আপনার মতামত পেয়েছি।',
    close: 'বন্ধ',
    error: 'একটি ত্রুটি ঘটেছে, পুনরায় চেষ্টা করুন।',
    msg_required: 'পাঠানোর আগে অনুগ্রহ করে আপনার বার্তা লিখুন',
  },
  zh: {
    button: '发送反馈',
    title: '分享您的想法',
    intro: '您的反馈帮助我们改进 Falak。每条消息我们都会阅读。',
    category: '反馈类型',
    cat_bug: '错误', cat_feature: '建议', cat_praise: '赞赏', cat_other: '其他',
    name: '您的姓名（可选）',
    email: '您的邮箱（可选）',
    email_hint: '以便我们回复您',
    message: '您的留言',
    msg_placeholder: '在此输入您的反馈...',
    send: '发送',
    sending: '发送中...',
    cancel: '取消',
    via_email: '改为通过邮件发送',
    success_title: '谢谢！🌙',
    success_msg: '我们已收到您的反馈，非常感谢。',
    close: '关闭',
    error: '出现错误，请重试。',
    msg_required: '发送前请填写您的留言',
  },
  fa: {
    button: 'ارسال بازخورد',
    title: 'نظرتان را با ما در میان بگذارید',
    intro: 'بازخورد شما به ما کمک می‌کند فلک را بهتر کنیم. هر پیامی خوانده می‌شود.',
    category: 'نوع بازخورد',
    cat_bug: 'خطا', cat_feature: 'پیشنهاد', cat_praise: 'تقدیر', cat_other: 'سایر',
    name: 'نام شما (اختیاری)',
    email: 'ایمیل شما (اختیاری)',
    email_hint: 'تا بتوانیم به شما پاسخ دهیم',
    message: 'پیام شما',
    msg_placeholder: 'بازخورد خود را اینجا بنویسید...',
    send: 'ارسال',
    sending: 'در حال ارسال...',
    cancel: 'انصراف',
    via_email: 'به‌جای آن از طریق ایمیل ارسال کن',
    success_title: 'متشکریم! 🌙',
    success_msg: 'بازخورد شما با موفقیت دریافت شد.',
    close: 'بستن',
    error: 'خطایی رخ داد، دوباره تلاش کنید.',
    msg_required: 'لطفاً قبل از ارسال پیام خود را بنویسید',
  },
  tr: {
    button: 'Geri Bildirim Gönder',
    title: 'Düşüncelerinizi paylaşın',
    intro: 'Geri bildiriminiz Falak\'ı geliştirmemize yardımcı olur. Her mesaj okunur.',
    category: 'Geri bildirim türü',
    cat_bug: 'Hata', cat_feature: 'Öneri', cat_praise: 'Övgü', cat_other: 'Diğer',
    name: 'Adınız (isteğe bağlı)',
    email: 'E-postanız (isteğe bağlı)',
    email_hint: 'Size yanıt verebilmemiz için',
    message: 'Mesajınız',
    msg_placeholder: 'Geri bildiriminizi buraya yazın...',
    send: 'Gönder',
    sending: 'Gönderiliyor...',
    cancel: 'İptal',
    via_email: 'Bunun yerine e-posta ile gönder',
    success_title: 'Teşekkürler! 🌙',
    success_msg: 'Geri bildiriminizi aldık. Çok teşekkür ederiz.',
    close: 'Kapat',
    error: 'Bir hata oluştu, lütfen tekrar deneyin.',
    msg_required: 'Lütfen göndermeden önce mesajınızı yazın',
  },
  es: {
    button: 'Enviar comentario',
    title: 'Comparte tu opinión',
    intro: 'Tus comentarios nos ayudan a mejorar Falak. Cada mensaje es leído.',
    category: 'Tipo de comentario',
    cat_bug: 'Error', cat_feature: 'Sugerencia', cat_praise: 'Elogio', cat_other: 'Otro',
    name: 'Tu nombre (opcional)',
    email: 'Tu correo (opcional)',
    email_hint: 'Para que podamos responderte',
    message: 'Tu mensaje',
    msg_placeholder: 'Escribe tu comentario aquí...',
    send: 'Enviar',
    sending: 'Enviando...',
    cancel: 'Cancelar',
    via_email: 'Enviar por correo en su lugar',
    success_title: '¡Gracias! 🌙',
    success_msg: 'Tu comentario ha sido recibido. Lo apreciamos de verdad.',
    close: 'Cerrar',
    error: 'Ha ocurrido un error, inténtalo de nuevo.',
    msg_required: 'Por favor escribe tu mensaje antes de enviar',
  },
  pt: {
    button: 'Enviar feedback',
    title: 'Compartilhe sua opinião',
    intro: 'Seu feedback nos ajuda a melhorar o Falak. Cada mensagem é lida.',
    category: 'Tipo de feedback',
    cat_bug: 'Erro', cat_feature: 'Sugestão', cat_praise: 'Elogio', cat_other: 'Outro',
    name: 'Seu nome (opcional)',
    email: 'Seu e-mail (opcional)',
    email_hint: 'Para que possamos responder',
    message: 'Sua mensagem',
    msg_placeholder: 'Escreva seu feedback aqui...',
    send: 'Enviar',
    sending: 'Enviando...',
    cancel: 'Cancelar',
    via_email: 'Enviar por e-mail em vez disso',
    success_title: 'Obrigado! 🌙',
    success_msg: 'Seu feedback foi recebido. Agradecemos muito.',
    close: 'Fechar',
    error: 'Ocorreu um erro, tente novamente.',
    msg_required: 'Por favor escreva sua mensagem antes de enviar',
  },
  de: {
    button: 'Feedback senden',
    title: 'Teilen Sie Ihre Meinung',
    intro: 'Ihr Feedback hilft uns, Falak zu verbessern. Jede Nachricht wird gelesen.',
    category: 'Feedback-Typ',
    cat_bug: 'Fehler', cat_feature: 'Vorschlag', cat_praise: 'Lob', cat_other: 'Sonstiges',
    name: 'Ihr Name (optional)',
    email: 'Ihre E-Mail (optional)',
    email_hint: 'Damit wir Ihnen antworten können',
    message: 'Ihre Nachricht',
    msg_placeholder: 'Schreiben Sie hier Ihr Feedback...',
    send: 'Senden',
    sending: 'Wird gesendet...',
    cancel: 'Abbrechen',
    via_email: 'Stattdessen per E-Mail senden',
    success_title: 'Danke! 🌙',
    success_msg: 'Ihr Feedback ist eingegangen. Wir schätzen es sehr.',
    close: 'Schließen',
    error: 'Ein Fehler ist aufgetreten, bitte erneut versuchen.',
    msg_required: 'Bitte schreiben Sie Ihre Nachricht, bevor Sie senden',
  },
  it: {
    button: 'Invia feedback',
    title: 'Condividi la tua opinione',
    intro: 'Il tuo feedback ci aiuta a migliorare Falak. Ogni messaggio viene letto.',
    category: 'Tipo di feedback',
    cat_bug: 'Errore', cat_feature: 'Suggerimento', cat_praise: 'Elogio', cat_other: 'Altro',
    name: 'Il tuo nome (opzionale)',
    email: 'La tua email (opzionale)',
    email_hint: 'Così possiamo risponderti',
    message: 'Il tuo messaggio',
    msg_placeholder: 'Scrivi qui il tuo feedback...',
    send: 'Invia',
    sending: 'Invio in corso...',
    cancel: 'Annulla',
    via_email: 'Invia via email invece',
    success_title: 'Grazie! 🌙',
    success_msg: 'Il tuo feedback è stato ricevuto. Lo apprezziamo davvero.',
    close: 'Chiudi',
    error: 'Si è verificato un errore, riprova.',
    msg_required: 'Per favore scrivi il tuo messaggio prima di inviare',
  }
};

const FeedbackButton = ({ textColors, selectedCity, inline = false }) => {
  const { i18n } = useTranslation();
  const lang = L[i18n.language] ? i18n.language : 'en';
  const t = L[lang];
  const isRTL = lang === 'ar' || lang === 'ur' || lang === 'fa';

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('other');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setCategory('other'); setName(''); setEmail(''); setMessage('');
    setSending(false); setSuccess(false); setError(null);
  };

  const handleClose = () => { setOpen(false); setTimeout(reset, 400); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // A written message is now required (star rating removed — it lives
    // in the Smart Review Modal which routes to the actual store).
    if (!message.trim()) { setError(t.msg_required); return; }
    setSending(true); setError(null);
    try {
      await axios.post(`${API}/feedback`, {
        name: name || null,
        email: email || null,
        rating: null,
        category,
        message: message.trim(),
        language: lang,
        city: selectedCity ? (isRTL ? selectedCity.name_ar : selectedCity.name_en) : null,
        user_agent: navigator.userAgent.slice(0, 200)
      });
      setSuccess(true);
    } catch (err) {
      setError(t.error);
    } finally { setSending(false); }
  };

  // Backup mailto fallback
  const buildMailto = () => {
    const subj = encodeURIComponent(`Falak Feedback — ${category}`);
    const body = encodeURIComponent(
      `${t.name}: ${name || '-'}\n${t.email}: ${email || '-'}\n${t.category}: ${t['cat_' + category]}\n\n${t.message}\n${message}`
    );
    return `mailto:ffalakkk@gmail.com?subject=${subj}&body=${body}`;
  };

  const categories = [
    { key: 'bug',     icon: Bug,           color: '#EF4444' },
    { key: 'feature', icon: Sparkles,      color: '#0EA5E9' },
    { key: 'praise',  icon: Heart,         color: '#EC4899' },
    { key: 'other',   icon: MoreHorizontal, color: '#94A3B8' },
  ];

  return (
    <>
      {/* === FLOATING BUTTON — hidden when rendered inside the drawer === */}
      {!inline && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-40 flex items-center gap-2 px-4 py-3 rounded-full backdrop-blur-xl transition-all hover:-translate-y-1 active:scale-95"
          style={{
            bottom: '20px',
            [isRTL ? 'left' : 'right']: '20px',
            background: 'rgba(11, 19, 43, 0.9)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#FFFFFF',
            boxShadow: '0 12px 32px rgba(0,0,0,0.45)'
          }}
          data-testid="feedback-button"
          aria-label={t.button}
        >
          <MessageSquare size={18} />
          <span className="font-medium text-sm hidden sm:inline">{t.button}</span>
        </button>
      )}

      {/* === INLINE BUTTON — used inside the hamburger drawer === */}
      {inline && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ color: '#F1F5F9', textAlign: isRTL ? 'right' : 'left' }}
          data-testid="feedback-button-inline"
        >
          {t.button}
        </button>
      )}

      {/* === MODAL === */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
          onClick={handleClose}
          data-testid="feedback-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl border overflow-hidden"
            style={{
              background: '#0B132B',
              borderColor: 'rgba(255,255,255,0.2)',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 24px 64px rgba(0,0,0,0.7)'
            }}
            onClick={(e) => e.stopPropagation()}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-3">
                <MessageSquare size={22} style={{ color: '#0EA5E9' }} />
                <h3 className="text-lg font-semibold text-white">{t.title}</h3>
              </div>
              <button
                onClick={handleClose}
                className="text-white/60 hover:text-white transition-colors p-1"
                data-testid="feedback-close"
                aria-label={t.close}
              >
                <X size={20} />
              </button>
            </div>

            {!success ? (
              <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="feedback-form">
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.intro}</p>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#CBD5E1' }}>{t.category}</label>
                  <div className="grid grid-cols-4 gap-2">
                    {categories.map(cat => {
                      const Icon = cat.icon;
                      const active = category === cat.key;
                      return (
                        <button
                          key={cat.key}
                          type="button"
                          onClick={() => setCategory(cat.key)}
                          className="flex flex-col items-center gap-1 p-3 rounded-xl transition-all"
                          style={{
                            background: active ? `${cat.color}26` : 'rgba(255,255,255,0.05)',
                            border: active ? `1px solid ${cat.color}` : '1px solid transparent',
                            color: active ? cat.color : 'rgba(255,255,255,0.7)'
                          }}
                          data-testid={`feedback-cat-${cat.key}`}
                        >
                          <Icon size={20} />
                          <span className="text-xs font-medium">{t['cat_' + cat.key]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Name + Email side by side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>{t.name}</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                      className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border focus:border-blue-400/60 text-white"
                      style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
                      data-testid="feedback-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>{t.email}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={200}
                      className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border focus:border-blue-400/60 text-white"
                      style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
                      data-testid="feedback-email"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#CBD5E1' }}>{t.message}</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t.msg_placeholder}
                    rows={5}
                    maxLength={5000}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border focus:border-blue-400/60 resize-none text-white"
                    style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
                    data-testid="feedback-message"
                  />
                  <div className="text-[10px] mt-1 text-right" style={{ color: 'rgba(255,255,255,0.4)' }} dir="ltr">
                    {message.length} / 5000
                  </div>
                </div>

                {error && (
                  <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.15)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.3)' }} data-testid="feedback-error">
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all disabled:opacity-50"
                    style={{ background: '#0EA5E9', color: '#FFFFFF' }}
                    data-testid="feedback-submit"
                  >
                    <Send size={16} />
                    {sending ? t.sending : t.send}
                  </button>
                  <a
                    href={buildMailto()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors hover:bg-white/5"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                    data-testid="feedback-mailto"
                  >
                    <Mail size={14} />
                    {t.via_email}
                  </a>
                </div>
              </form>
            ) : (
              <div className="p-8 text-center" data-testid="feedback-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(16,185,129,0.18)' }}>
                  <Check size={32} style={{ color: '#10B981' }} strokeWidth={2.5} />
                </div>
                <h4 className="text-xl font-semibold mb-2 text-white">{t.success_title}</h4>
                <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.75)' }}>{t.success_msg}</p>
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 rounded-lg font-medium transition-colors"
                  style={{ background: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}
                  data-testid="feedback-success-close"
                >
                  {t.close}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
